import { useQuery } from '@tanstack/react-query'
import { createApiCall } from 'services'

/**
 * VRG-491 — re-exported from `utils/invalidScanSeverity`, which is now the single union on
 * this side of the wire. The copy that used to live here had drifted: it was missing
 * `tag_replaced` (VRG-268) and `tag_held` (VRG-275), both of which the server writes and
 * the `scans_invalid_reason_check` constraint permits, so a row carrying either widened to
 * `null` at the type level and any exhaustive switch over this union was quietly incomplete.
 */
export type { InvalidReason } from 'utils/invalidScanSeverity'
import type { InvalidReason } from 'utils/invalidScanSeverity'

export type CertificateScanRecord = {
  id: string
  global_tag_id: string | null
  verified: boolean
  invalid: boolean
  invalid_reason: InvalidReason | null
  /**
   * VRG-252 — what the scanned tag's family can prove AT ALL. `static` marks a Connect tap: read
   * and resolved, no per-tap proof possible. Derived server-side from the tag family and sent
   * INSTEAD of it, because the chip family must never reach a customer surface and this record
   * feeds the shared resolver that renders on the consumer app too.
   */
  assurance?: 'cryptographic' | 'static' | null
  /** Verification-event stage discriminator (VRG-106): unset/'tag_verify' for
   * physical scan rows; 'render_resolve' for render-time `vt` check failures
   * (NOT a physical scan — see isRenderResolve). */
  kind?: string | null
  /** Granular render-token failure reason — only set when kind === 'render_resolve':
   * 'vt_expired' | 'vt_bad_signature' | 'vt_wrong_purpose' | 'vt_cert_mismatch' | 'vt_malformed'. */
  render_reason?: string | null
  lat: number | null
  lng: number | null
  geo_country: string | null
  geo_city: string | null
  created_at: string
}

/** True when a scan-map/list row is a render-token (`vt`) verification EVENT
 * (VRG-106) rather than a physical scan. */
export const isRenderResolve = (
  scan?: { kind?: string | null } | null,
): boolean => scan?.kind === 'render_resolve'

/** Suspicious render-token failures (forged / wrong-purpose / cross-cert replay) are
 * tinted distinctly from the benign 'vt_expired' re-view case — mirrors the granular
 * reason table in VRG-106. */
export const renderResolveMeta = (
  reason?: string | null,
): { key: string; label: string; color: string; suspicious: boolean } => {
  switch (reason) {
    case 'vt_bad_signature':
    case 'vt_wrong_purpose':
    case 'vt_cert_mismatch':
      return { key: 'link_suspicious', label: 'Suspicious link', color: '#DB2777', suspicious: true }
    case 'vt_expired':
      return { key: 'link_stale', label: 'Stale link', color: '#71717A', suspicious: false }
    default:
      return { key: 'link_invalid', label: 'Invalid link', color: '#71717A', suspicious: false }
  }
}

export const useCertificateScanRecords = (certificateId: string) => {
  return useQuery<CertificateScanRecord[]>({
    queryKey: ['certificate-scan-records', certificateId],
    queryFn: async (): Promise<CertificateScanRecord[]> => {
      const response = await createApiCall({
        method: 'GET',
        url: `/scan-log/public/${certificateId}/records`,
      })
      return (response.data?.data ?? []) as CertificateScanRecord[]
    },
    enabled: Boolean(certificateId),
    // VRG-159: near-real-time Scans table. Poll every 5s so new scans appear live without
    // a manual refresh. Paused while the tab is backgrounded (no wasted load on hidden
    // tabs) and fetched immediately on re-focus to catch up. Instant-latency SSE push is
    // the tracked improvement (VRG-159).
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
}
